import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useTurningDetection } from '../../hooks/useDogDetector';
import { drawDetectionOnCanvas, type DogDetection } from '../../services/dogDetector';
import { serverDetectionToDogDetection } from '../../services/detectionMapper';
import { getTransport } from '../../transport';
import { dashboardService } from '../../services/dashboard';
import DetectionModelSelector from './DetectionModelSelector';

const API_URL = import.meta.env.VITE_API_URL || window.location.origin;

interface RecentResultRow {
  cls: string;
  confidence: number;
  ts: number;
}

const CLASS_LABELS: Record<string, string> = {
  'cat-face': 'Cat Face',
  'cat-pose': 'Cat Body',
  'cat-eye': 'Cat Eye',
  'dog': 'Dog',
};

function bestDetectionOf(
  entries: Array<{ cls: string; confidence: number; ts: number }>,
  ts: number,
): RecentResultRow | null {
  if (entries.length === 0) return null;
  const best = entries.reduce((a, b) => (b.confidence > a.confidence ? b : a));
  return { cls: best.cls, confidence: best.confidence, ts };
}

const ObjectDetectionPanel: React.FC = () => {
  const images = useSelector((state: RootState) => state.dashboard.images);

  const [latestImageUrl, setLatestImageUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [lastDetection, setLastDetection] = useState<DogDetection | null>(null);
  const [modelOnline, setModelOnline] = useState(false);
  const [lastModule, setLastModule] = useState<string | null>(null);
  const [lastResultAt, setLastResultAt] = useState<number | null>(null);
  const [lastFgs, setLastFgs] = useState<any | null>(null);
  const [lastPainScore, setLastPainScore] = useState<any | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [latestResult, setLatestResult] = useState<RecentResultRow | null>(null);
  const [totalDetections, setTotalDetections] = useState(0);
  const lastModuleRef = useRef<string | null>(null);
  const [lastBehavior, setLastBehavior] = useState<{ label: string; confidence: number } | null>(null);
  const [lastOrientation, setLastOrientation] = useState<{ angle: number; circling: boolean } | null>(null);

  // Turning detection hook (pure logic — no TF.js dependency)
  const { turningState, addDetection, reset: resetTurning } = useTurningDetection();

  // Get latest image URL for display
  useEffect(() => {
    if (images.length > 0) {
      const latestImage = images[images.length - 1];
      if (latestImage.image) {
        setLatestImageUrl(latestImage.image);
      } else if (latestImage.sessionId && latestImage.filename) {
        const accessToken = dashboardService.getAccessToken() || localStorage.getItem('auth_token');
        if (accessToken) {
          const url = `${API_URL}/api/dashboard/images/${latestImage.sessionId}/${latestImage.filename}?token=${accessToken}`;
          setLatestImageUrl(url);
        }
      } else {
        setLatestImageUrl(null);
      }
    }
  }, [images]);

  // Listen for detection:result WS events from the model server (via Node)
  useEffect(() => {
    const transport = getTransport();
    if (!transport) return;

    const cleanup = transport.onEvent('detection:result', (payload: any) => {
      if (payload.module && payload.module !== lastModuleRef.current) {
        lastModuleRef.current = payload.module;
        setLastModule(payload.module);
        setLastDetection(null);
        setLastBehavior(null);
        setLastOrientation(null);
        setLastFgs(null);
        setLastPainScore(null);
        setLatestResult(null);
        setTotalDetections(0);
        resetTurning();
      }

      if (payload.success === false) {
        setLastError(payload.error || 'Detection failed');
        return;
      }

      setLastError(null);
      setModelOnline(true);
      setLastResultAt(Date.now());

      const detections = payload.detections || [];
      if (detections.length > 0) {
        setTotalDetections((n) => n + detections.length);
        const frameStamp = payload.timestamp || Date.now();
        const frameEntries = detections.map((d: any) => ({
          cls: d.class || 'unknown',
          confidence: d.bbox?.confidence ?? d.confidence ?? 0,
          ts: frameStamp,
        }));
        setLatestResult(bestDetectionOf(frameEntries, frameStamp));
      }

      if (payload.fgs) {
        setLastFgs(payload.fgs);
      } else {
        setLastFgs(null);
      }
      if (payload.painScore) {
        setLastPainScore(payload.painScore);
      } else {
        setLastPainScore(null);
      }

      if (detections.length === 0) {
        setLastDetection(null);
        setLastBehavior(null);
        setLastOrientation(null);
        return;
      }

      const serverDet = detections[0] as any;
      const dogDetection = serverDetectionToDogDetection(serverDet);

      if (serverDet.behavior) {
        setLastBehavior({ label: serverDet.behavior.label, confidence: serverDet.behavior.confidence });
      }
      if (serverDet.orientation) {
        setLastOrientation({ angle: serverDet.orientation.angle, circling: serverDet.orientation.circling });
      }

      if (dogDetection && dogDetection.isValid && dogDetection.facing) {
        setLastDetection(dogDetection);
        addDetection(dogDetection, payload.timestamp || Date.now());
      } else if (dogDetection) {
        setLastDetection(dogDetection);
      }
    });

    return () => cleanup();
  }, [addDetection]);

  // Draw image + detection overlay on canvas when image loads or detection updates
  const drawDetection = useCallback((image: HTMLImageElement) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const modelSize = 640;
    const imgWidth = image.naturalWidth;
    const imgHeight = image.naturalHeight;
    const imgAspect = imgWidth / imgHeight;

    const containerWidth = canvas.clientWidth || 640;
    const containerHeight = canvas.clientHeight || 256;
    const containerAspect = containerWidth / containerHeight;

    let drawWidth, drawHeight, offsetX, offsetY;
    if (imgAspect > containerAspect) {
      drawWidth = containerWidth;
      drawHeight = containerWidth / imgAspect;
      offsetX = 0;
      offsetY = (containerHeight - drawHeight) / 2;
    } else {
      drawHeight = containerHeight;
      drawWidth = containerHeight * imgAspect;
      offsetX = (containerWidth - drawWidth) / 2;
      offsetY = 0;
    }

    canvas.width = containerWidth;
    canvas.height = containerHeight;

    // Always draw the image first
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, containerWidth, containerHeight);
    ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);

    // Draw detection overlay if available
    if (lastDetection) {
      const scaleX = drawWidth / modelSize;
      const scaleY = drawHeight / modelSize;
      ctx.save();
      ctx.translate(offsetX, offsetY);
      drawDetectionOnCanvas(ctx, lastDetection, { scaleX, scaleY, simpleMode: true, drawBbox: true });
      ctx.restore();
    }
  }, [lastDetection]);

  // Redraw when detection updates
  useEffect(() => {
    if (imageRef.current && lastDetection) {
      drawDetection(imageRef.current);
    }
  }, [lastDetection, drawDetection]);

  const confidenceColor = (conf: number) => {
    if (conf >= 0.9) return 'text-green-500';
    if (conf >= 0.7) return 'text-yellow-500';
    return 'text-red-500';
  };

  const directionEmoji = (direction: string): string => {
    const emoji4: Record<string, string> = {
      'right': '➡️',
      'down': '⬇️',
      'left': '⬅️',
      'up': '⬆️',
    };
    return emoji4[direction] || '➡️';
  };

  return (
    <div className="bg-white dark:bg-claude-darkSurface rounded-xl p-4 shadow-sm border border-gray-200 dark:border-claude-darkBorder">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-claude-darkText">Live Detection</h3>
            <p className="text-sm text-gray-500 dark:text-claude-darkTextSecondary">
              {totalDetections} detections
              {modelOnline ? ' • Model server online' : ' • Model server offline'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {lastError
                ? `Error: ${lastError}`
                : lastModule
                  ? `Active model: ${lastModule}`
                  : 'Active model: server default (dog-pose)'}
              {lastResultAt && !lastError && ` • updated ${new Date(lastResultAt).toLocaleTimeString()}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DetectionModelSelector />
          {turningState.isSpinning && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium animate-pulse">
              🔄 Spinning!
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <div className="relative bg-gray-900 dark:bg-black rounded-lg h-64 overflow-hidden">
            {latestImageUrl ? (
              <div className="relative w-full h-full">
                <img
                  ref={imageRef}
                  src={latestImageUrl}
                  alt="Camera feed"
                  className="hidden"
                  crossOrigin="anonymous"
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    imageRef.current = img;
                    drawDetection(img);
                  }}
                />
                <canvas
                  ref={canvasRef}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <svg className="w-16 h-16 mx-auto text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-sm">Waiting for images...</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Behavior + Orientation */}
        {lastBehavior && (
          <div className="col-span-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-3xl">
                  {lastBehavior.label === 'sit' ? '🪑' : lastBehavior.label === 'stand' ? '🧍' :
                   lastBehavior.label === 'lie' ? '🛏️' : lastBehavior.label === 'scratch' ? '🐾' :
                   lastBehavior.label === 'walk' ? '🚶' : lastBehavior.label === 'unsure' ? '❓' : '🐶'}
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Behavior</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                    {lastBehavior.label}
                  </div>
                  {lastOrientation && (
                    <div className="text-xs text-blue-500">
                      Orientation: {lastOrientation.angle.toFixed(1)}°
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500 dark:text-gray-400">Confidence</div>
                <div className={`text-lg font-semibold ${confidenceColor(lastBehavior.confidence)}`}>
                  {(lastBehavior.confidence * 100).toFixed(0)}%
                </div>
              </div>
            </div>
            {lastOrientation?.circling && (
              <div className="mt-2 flex items-center gap-2 text-sm text-red-500 font-medium animate-pulse">
                🔄 Circling detected!
              </div>
            )}
          </div>
        )}

        {/* FGS Cat Pain Analysis */}
        {lastFgs && (
          <div className="col-span-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="text-3xl">🐱</div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Feline Pain Analysis (FGS)</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {lastFgs.total} / {lastFgs.maxTotal}
                  </div>
                </div>
              </div>
              <div className="text-right">
                {lastFgs.analgesiaRecommended && (
                  <span className="inline-flex px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium">
                    Analgesia recommended
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{lastFgs.summary}</p>
            {lastFgs.actionUnits && (
              <div className="mt-2 grid grid-cols-5 gap-2 text-center">
                {Object.entries(lastFgs.actionUnits).map(([unit, info]: [string, any]) => (
                  <div key={unit} className="bg-white dark:bg-gray-800 rounded-md p-1.5">
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{info.score ?? 0}</div>
                    <div className="text-[10px] text-gray-400 truncate" title={unit}>{unit}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Legacy pain score (0-6/0-8) */}
        {lastPainScore && (
          <div className="col-span-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Cat Pain Score</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {lastPainScore.totalPainScore} / {lastPainScore.maxPainScore}
                </div>
              </div>
              <div className="flex gap-3 text-xs text-gray-500 dark:text-gray-400">
                <span>ear {lastPainScore.earPainScore}</span>
                <span>muzzle {lastPainScore.muzzlePainScore}</span>
                <span>eye {lastPainScore.eyePainScore}</span>
              </div>
            </div>
          </div>
        )}

        {/* Facing Direction */}
        {lastDetection && lastDetection.facing && (
          <div className="col-span-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-3xl">{directionEmoji(lastDetection.facing.direction)}</div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Facing Direction</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                    {lastDetection.facing.direction.replace('-', ' ')}
                  </div>
                  <div className="text-xs text-gray-400">
                    {lastDetection.facing.angleDegrees.toFixed(1)}°
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500 dark:text-gray-400">Confidence</div>
                <div className={`text-lg font-semibold ${confidenceColor(lastDetection.facing.confidence)}`}>
                  {(lastDetection.facing.confidence * 100).toFixed(0)}%
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Turning Stats */}
        {turningState.directionHistory.length > 0 && (
          <div className="col-span-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Direction</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                  {turningState.rotationDirection !== 'none' ? turningState.rotationDirection : '-'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Rotation</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {turningState.netRotation > 0 ? '+' : ''}{Math.abs(turningState.netRotation * 90)}°
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Turns</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {turningState.turnsCount}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Status</div>
                <div className={`text-lg font-semibold ${turningState.isSpinning ? 'text-red-500' : turningState.isTurning ? 'text-yellow-500' : 'text-green-500'}`}>
                  {turningState.isSpinning ? 'Spinning!' : turningState.isTurning ? 'Turning' : 'Normal'}
                </div>
              </div>
            </div>
            {turningState.directionHistory.length > 1 && (
              <div className="mt-2 text-xs text-gray-400 text-center">
                {turningState.directionHistory.slice(-5).map(h => directionEmoji(h.direction)).join(' → ')}
              </div>
            )}
          </div>
        )}

        {latestResult && (
          <div className="col-span-2 flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-purple-500"></span>
              <span className="text-gray-900 dark:text-claude-darkText">
                {CLASS_LABELS[latestResult.cls] || latestResult.cls}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className={`font-medium ${confidenceColor(latestResult.confidence)}`}>
                {(latestResult.confidence * 100).toFixed(0)}%
              </span>
              <span className="text-xs text-gray-400">
                {new Date(latestResult.ts).toLocaleTimeString()}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ObjectDetectionPanel;